import React, { useEffect, useState, useCallback } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { alpha } from '@mui/system/colorManipulator';
import { useSelector, shallowEqual } from 'react-redux';
import { useTheme } from '@mui/system';
import { useFormContext, useFieldArray } from 'react-hook-form';
import CargoItem from '#component/shipment/form/cargo/cargo-item/cargo-item';
import { setAfterSubmitAction, nextStep } from '#action/shipment/form';
import { submitAndGoBack } from '#component/shipment/form/use-step-submit';
import {
  StyledPaper,
  AddBtnCnt,
  AddBtn,
  ButtonCnt,
  StyledButton
} from './style';
import { useIntersection } from '#hooks';
import { uuidv4 } from '#helper';

const CargoItems = props => {
  const { fields, remove, error, disabled, isCopiedShipment } = props;
  const { goods, containers, id } = useSelector(s => s.shipment, shallowEqual);
  const [expanded, setExpanded] = useState(fields?.length ? `cargo[${fields.length - 1}]` : false);

  const handleSetExpanded = useCallback(panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  useEffect(() => {
    if (fields?.length) {
      setExpanded(`cargo[${fields.length - 1}]`);
    }
  }, [fields?.length]);

  return (
    <>
      {fields && fields.map((field, index, self) => (
        <CargoItem
          key={field.id || index}
          item={field}
          index={index}
          member={`cargo[${index}]`}
          goods={goods}
          containers={containers}
          remove={remove}
          numberOfItems={self.length}
          disabled={disabled}
          expanded={expanded === `cargo[${index}]`}
          handleSetExpanded={handleSetExpanded}
          shipmentId={id}
          isCopiedShipment={isCopiedShipment}
        />
      ))}
      {error && <Typography align="center" color="error">{error}</Typography>}
    </>
  );
};

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

const Form = props => {
  const theme = useTheme();
  const methods = useFormContext();
  const { control, handleSubmit, formState } = methods || {};
  const { fields, append, remove } = useFieldArray({ control, name: 'cargo' });
  const cargoErrors = formState?.errors?.cargo;
  const groupError = cargoErrors?.root?.message ?? cargoErrors?.message;

  const {
    submitting,
    shipment,
    dispatch,
    editable,
    isCopiedShipment,
    isCreateMode,
    SaveButton = () => null,
    CancelButton = () => null,
    onFormSubmit
  } = props;

  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);

  const addCargoItem = () => {
    append({ hazmat: 0, id: uuidv4() });
  };

  const onBackClick = () =>
    submitAndGoBack(dispatch, methods, onFormSubmit, { validate: !isCreateMode });

  const onNextClick = () => {
    const next = () => {
      dispatch(nextStep());
    };

    dispatch(setAfterSubmitAction(next));
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit?.(onFormSubmit)}>
      <StyledPaper elevation={0}>
        <CargoItems
          fields={fields}
          remove={remove}
          error={groupError}
          disabled={!editable}
          isCopiedShipment={isCopiedShipment}
        />
        <AddBtnCnt>
          <AddBtn
            type="button"
            size="small"
            disabled={!editable}
            onClick={addCargoItem}
          >
            <Tooltip title="Add cargo">
              <AddCircleOutlineRoundedIcon
                style={{ color: alpha(theme.palette.secondary.main, editable ? 1 : 0.6) }}
              />
            </Tooltip>
          </AddBtn>
        </AddBtnCnt>
      </StyledPaper>
      <div ref={sentinelRef} />
      <ButtonCnt hasBorderTop={!isIntersecting}>
        <StyledButton as={CancelButton} />
        <StyledButton
          disabled={submitting}
          type="button"
          variant="outlined"
          onClick={onBackClick}
        >
          Back
        </StyledButton>
        <StyledButton
          disabled={submitting}
          variant="outlined"
          type="submit"
          onClick={onNextClick}
        >
          Next
        </StyledButton>
        {shipment.id && editable && (
          <StyledButton as={SaveButton} disabled={submitting} />
        )}
      </ButtonCnt>
    </form>
  );
};

export default Form;
